import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    BUILDING_NAMES,
    DEFAULT_SCIENCES,
    SCIENCE_DEFINITIONS,
    TEAM_PLANET_SCIENCE_QUEUE_LIMIT,
    type Planet,
    type ScienceId,
    type TickCheckpointEventOf,
} from '@kaeliux/shared';
import { extractBuildingLevels, extractScienceLevels } from '@kaeliux/game-engine';
import { useAuthStore } from '../store/authStore';
import { useUniverseStore } from '../store/universeStore';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { useTeamPlanetSession } from '../contexts/TeamPlanetContext';
import { usePlanetView } from '../contexts/PlanetViewContext';
import { useNow } from '../hooks/useNow';
import { useStoreTeamPlanet } from '../hooks/useTeamPlanet';
import { cancelTeamPlanetScience, queueTeamPlanetScience } from '../services/teamPlanet.service';
import ScienceCard, { SCIENCE_CATEGORY_ORDER, SCIENCE_CATEGORY_TITLES } from '../components/ScienceCard';
import QueueItemCard from '../components/QueueItemCard';
import TeamPlanetReadOnlyNotice from '../components/teamPlanet/TeamPlanetReadOnlyNotice';
import { Panel } from '../components/ui/Panel';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import { zoneAccentStyle } from '../components/planet/zoneAccent';
import { getScienceIcon } from '../utils/scienceIcons';
import { getBuildingIcon } from '../utils/buildingIcons';
import { eventProgress } from '../utils/eventProgress';
import { formatCountdown } from '../utils/formatters';
import { cn } from '../utils/cn';

interface QueuedTeamResearchProps {
    event: TickCheckpointEventOf<'scienceResearch'>;
    now: number;
    canManage: boolean;
    onUpdated: (planet: Planet) => Promise<void>;
}

/** A queued/researching Team planet science. No Rush: the Team planet has no premium shortcuts. */
function QueuedTeamResearch({ event, now, canManage, onUpdated }: QueuedTeamResearchProps) {
    const { openDialog } = useConfirmDialog();
    const [isCancelling, setIsCancelling] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const def = SCIENCE_DEFINITIONS[event.eventDetails.scienceId];
    const startMs = event.eventDetails.startDate.getTime();
    const endMs = event.endDate.getTime();
    const remainingMs = endMs - now;

    async function doCancel() {
        setIsCancelling(true);
        setError(null);
        try {
            await onUpdated(await cancelTeamPlanetScience(event.eventId));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Cancel failed');
        } finally {
            setIsCancelling(false);
        }
    }

    return (
        <QueueItemCard
            image={<img src={getScienceIcon(event.eventDetails.scienceId)} alt="" className="w-full h-full object-cover rounded-tile bg-surface-void" />}
            overlayLabel={`Level ${event.eventDetails.targetLevel}`}
            name={def.name}
            statusText={remainingMs > 0 ? `${startMs <= now ? 'Researching…' : 'Queued'} ${formatCountdown(remainingMs)}` : 'Finishing…'}
            progress={eventProgress(startMs, endMs, now)}
            error={error}
            isCancelling={isCancelling}
            imageBoxClassName="w-14 aspect-square"
            onCancel={canManage ? () => openDialog({
                title: `Cancel ${def.name} research?`,
                message: 'The Team planet is refunded 60% of the resources spent.',
                confirmLabel: 'Cancel research',
                danger: true,
                onConfirm: () => { doCancel().catch(() => { }); },
            }) : undefined}
        />
    );
}

/**
 * The Team planet's Research Center: its own collective sciences,
 * separate from every member's personal ones, researched by the leader and
 * officers into a shared queue.
 */
export default function TeamPlanetSciencesPage() {
    const { teamPlanet, canManage } = useTeamPlanetSession();
    const view = usePlanetView();
    const store = useStoreTeamPlanet();
    const { profile } = useAuthStore();
    const { speedMultiplier } = useUniverseStore();
    const now = useNow();
    const [searchParams] = useSearchParams();
    const highlightScienceId = searchParams.get('highlight');

    const scienceLevels = useMemo(
        () => extractScienceLevels(teamPlanet.sciences ?? DEFAULT_SCIENCES),
        [teamPlanet.sciences],
    );
    const buildingLevels = useMemo(() => extractBuildingLevels(teamPlanet.buildings), [teamPlanet.buildings]);
    const queue = useMemo(
        () => (teamPlanet.tickCheckpoints ?? []).filter(
            (e): e is TickCheckpointEventOf<'scienceResearch'> => e.eventType === 'scienceResearch' && !e.cancelled,
        ),
        [teamPlanet.tickCheckpoints],
    );
    const queueFull = queue.length >= TEAM_PLANET_SCIENCE_QUEUE_LIMIT;
    const laboratoryLevel = buildingLevels.laboratory ?? 0;

    // A requirement tile elsewhere on the Team planet links here with
    // `?highlight=`; bring that card into view once the cards exist.
    const cardsRendered = laboratoryLevel > 0;
    useEffect(() => {
        if (!cardsRendered || !highlightScienceId) return;
        document.querySelector(`[data-tutorial-id="science-${CSS.escape(highlightScienceId)}"]`)
            ?.scrollIntoView({ block: 'center' });
    }, [highlightScienceId, cardsRendered]);
    const resources = {
        metal: teamPlanet.resources.metal.current,
        minerals: teamPlanet.resources.minerals.current,
        gas: teamPlanet.resources.gas.current,
        energy: teamPlanet.resources.energy.rate - (teamPlanet.resources.energy.consumed ?? 0),
        ozone: teamPlanet.state.ozone.current,
    };

    return (
        <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4" style={zoneAccentStyle('military')} data-testid="team-planet-sciences-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <img
                        src={getBuildingIcon('laboratory', laboratoryLevel, view.buildingRace('laboratory') ?? profile?.race)}
                        alt=""
                        className="h-14 w-11 rounded-tile border border-hairline bg-space-950 object-cover"
                    />
                    <div>
                        <h1 className="text-display text-content-primary">Alliance Research</h1>
                        <p className="text-body text-content-muted">Raises the Team planet&apos;s own science levels, separate from every member&apos;s.</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-body text-content-muted tabular-nums" data-testid="team-planet-research-queue-count">
                        Queue: {queue.length} / {TEAM_PLANET_SCIENCE_QUEUE_LIMIT}
                    </span>
                    <Link to="/team-planet" className={linkButtonClasses('secondary')}>← Back to Team Planet</Link>
                </div>
            </div>

            {!canManage && <TeamPlanetReadOnlyNotice>{'Your alliance leader and officers choose the Team planet\'s research.'}</TeamPlanetReadOnlyNotice>}

            {laboratoryLevel === 0 ? (
                <Panel variant="solid" padding="md" className="flex flex-col gap-2" data-testid="team-planet-no-laboratory">
                    <h2 className="text-title text-content-primary">No {BUILDING_NAMES.laboratory} yet</h2>
                    <p className="text-body text-content-muted">Construct the {BUILDING_NAMES.laboratory} on the Team planet to start alliance research.</p>
                    <Link to={view.zoneRoute('military', 'laboratory')} className={cn(linkButtonClasses('secondary', 'sm'), 'self-start')}>
                        Go to the Military zone
                    </Link>
                </Panel>
            ) : (
                SCIENCE_CATEGORY_ORDER.map((category) => (
                    <section key={category} className="flex flex-col gap-3">
                        <h2 className="text-heading text-content-secondary">{SCIENCE_CATEGORY_TITLES[category]}</h2>
                        {Object.values(SCIENCE_DEFINITIONS).filter((def) => def.category === category).map((def) => {
                            const events = queue.filter((e) => e.eventDetails.scienceId === def.id);
                            return (
                                <ScienceCard
                                    key={def.id}
                                    scienceId={def.id as ScienceId}
                                    race={profile?.race}
                                    resources={resources}
                                    laboratoryLevel={laboratoryLevel}
                                    speedMultiplier={speedMultiplier}
                                    scienceLevels={scienceLevels}
                                    buildingLevels={buildingLevels}
                                    queuedCount={events.length}
                                    queueFull={queueFull}
                                    highlighted={def.id === highlightScienceId}
                                    canManage={canManage}
                                    onResearch={async () => store(await queueTeamPlanetScience(def.id))}
                                    queueItems={events.length > 0 ? events.map((event) => (
                                        <QueuedTeamResearch key={event.eventId} event={event} now={now} canManage={canManage} onUpdated={store} />
                                    )) : undefined}
                                />
                            );
                        })}
                    </section>
                ))
            )}
        </main>
    );
}
